import Box from "@mui/material/Box"
import Grid from "@mui/material/Grid"
import IconButton from "@mui/material/IconButton"
import Typography from "@mui/material/Typography"
import Alert from "@mui/material/Alert"
import Checkbox from "@mui/material/Checkbox"
import Button from "@mui/material/Button"
import Tooltip from "@mui/material/Tooltip"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import type { GridColDef, GridPaginationModel, GridRowId } from "@mui/x-data-grid"
import React, { Suspense, useEffect, useMemo, useState, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useParams, Link } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useAuth0 } from "@auth0/auth0-react"
import { useAppSelector } from "@/store/hooks"
import { setCurrentWalkdownInfo, clearCurrentWalkdownInfo } from "@/store/walkdownSlice"
import ShimmerDataGrid from "../components/common/shimmer-data-grid"
import ShimmerChartCard from "../components/common/shimmer-card-chart"
import type { WalkdownDevice, WalkdownFilters } from "@/types/walkdown"
import WalkdownImagesSection from "@/components/walkdowns/WalkdownImagesSection"
import type { LifecycleStatusCardData } from "@/components/PieChartCard/PieChartCard"
import { transformDeviceTypeData, transformRatingsData, transformRecommendationsData, type ChartDataPoint, type DeviceTypeCategoryData } from "@/utils/walkdownChartTransform"
import WalkdownService from "@/services/walkdowns"
import DeviceService from "@/services/devices"
import iconEdit from "@/assets/icon-edit.svg"
import iconImageView from "@/assets/icon--image-view.svg"
import WalkdownLinkDevicePopup from "@/components/walkdowns/WalkdownLinkDevicePopup"
import MiniLoader from "@/components/MiniLoader"
import { usePermissions } from "@/hooks/usePermissions"
import MessageModal from "@/components/common/modals/MessageModal"
import BreadcrumbsComponent from "@/components/Breadcrumbs"
import LoadingFallback from "@/components/LoadingFallback"
import FullPageLoader from "@/components/FullPageLoader"
import CommonDataGrid from "@/components/common/CommonDataGrid"
import {
  selectSelectedSiteName,
  selectSelectedGDM
} from '@/store/selectors';

// Lazy load components
const WalkdownDetailsHeader = React.lazy(() => import("@/components/walkdowns/WalkdownDetailsHeader"))
const LifecycleStatusCard = React.lazy(() => import("@/components/PieChartCard/PieChartCard"))

interface WalkdownState {
  loading: boolean
  error: string | null
  chartLoading: boolean
  chartError: string | null
  walkdown: any
  walkdownNumbers: string[]
  devices: WalkdownDevice[]
  filteredDevices: WalkdownDevice[]
  selectedRowIds: GridRowId[]
  selectedDevice: WalkdownDevice | null
  openImageModal: boolean
  openLinkDevicePopup: boolean
  anchorEl: HTMLElement | null
  successWalkdownLinkDevice: boolean
  successWalkdownUnlinkDevice: boolean
  chartData: {
    deviceType: LifecycleStatusCardData[]
    ratings: LifecycleStatusCardData[]
    recommendations: LifecycleStatusCardData[]
  }
  originalDeviceTypeCategoryData: DeviceTypeCategoryData[]
}

// Helper: get highest percentage and its name for chart data
function getHighestPercentAndName(chartData: LifecycleStatusCardData[]): { percent: string, name: string } {
  if (!chartData || chartData.length === 0) return { percent: "0%", name: "-" };
  const total = chartData.reduce((sum, item) => sum + (item.value || 0), 0);
  if (total === 0) return { percent: "0%", name: "-" };
  const maxItem = chartData.reduce((max, item) => item.value > max.value ? item : max, chartData[0]);
  const pct = Math.round((maxItem.value / total) * 100);
  return { percent: `${pct}%`, name: maxItem.name || "-" };
}

const WalkdownDetails: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch()
  const { getAccessTokenSilently } = useAuth0()
    const chartDataFetchedRef = useRef(false)
  const walkdownDetailsFetchedRef = useRef(false)
  const lastWalkdownDetailsKeyRef = useRef<string | null>(null);
  const lastChartDataKeyRef = useRef<string | null>(null);
  const { getFeatureActions } = usePermissions()
  
  // Get permissions for walkdown detail page features
  const generateQRCodePermission = getFeatureActions('Walkdown', 'Generate Walkdown QR Code', 'Walkdown Detail Page')
  const walkdownDeviceLinkingPermission = getFeatureActions('Walkdown', 'Walkdown Device Linking', 'Walkdown Detail Page')  
  const multiSelectCheckboxPermission = getFeatureActions('Walkdown', 'Enabled Multi Select Checkbox', 'Walkdown List')
  
  // Get selected site details using selectors
  const siteName = useAppSelector(selectSelectedSiteName);
  const gdmNumber = useAppSelector(selectSelectedGDM);
  const userData = useAppSelector((state) => state.user.data);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 25,
  })
  const [state, setState] = useState<WalkdownState>({
    loading: true,
    error: null,
    chartLoading: false,
    chartError: null,
    walkdown: null,
    walkdownNumbers: [],
    devices: [],
    filteredDevices: [],
    selectedRowIds: [],
    selectedDevice: null,
    openImageModal: false,
    successWalkdownLinkDevice: false,
    successWalkdownUnlinkDevice: false,
    openLinkDevicePopup: false,
    anchorEl: null,
    chartData: {
      deviceType: [],
      ratings: [],
      recommendations: [],
    },
    originalDeviceTypeCategoryData: [],
  })

  const [initialLoading, setInitialLoading] = useState(true);
  const [walkdownDates, setWalkdownDates] = useState<string[]>([])
  
  // QR Code request states
  const [qrCodeRequesting, setQrCodeRequesting] = useState(false)
  const [qrCodeRequested, setQrCodeRequested] = useState(false)
  const [showUnlinkSuccess, setShowUnlinkSuccess] = useState(false);
  // Chart filter states
  const [deviceTypeCategoryFilter, setDeviceTypeCategoryFilter] = useState<string | null>(null)
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string | null>(null)
  const [ratingFilter, setRatingFilter] = useState<string | null>(null)
  const [recommendationFilter, setRecommendationFilter] = useState<string | null>(null)
  // Chart loading function
  const loadChartData = async (walkdownIds: string[]) => {
    const fetchKey = `${walkdownIds.join(',')}|${gdmNumber}`;
    if (lastChartDataKeyRef.current === fetchKey && chartDataFetchedRef.current) return;
    lastChartDataKeyRef.current = fetchKey;
    chartDataFetchedRef.current = true;
    try {
      const accessToken = await getAccessTokenSilently();      
      setState((prev) => ({ ...prev, chartLoading: true, chartError: null }))
      const walkdownService = new WalkdownService(undefined, accessToken)
      const chartData = await walkdownService.getWalkdownCharts(walkdownIds)

      if (chartData) {
        const deviceTypes = transformDeviceTypeData(chartData.device_type_chart_data)
        const ratings = transformRatingsData(chartData.device_rating_chart_data)
        const recommendations = transformRecommendationsData(chartData.device_recommendation_chart_data)

        setState((prev) => ({
          ...prev,
          chartLoading: false,
          chartData: {
            deviceType: deviceTypes,
            ratings: ratings,
            recommendations: recommendations,
          },
          originalDeviceTypeCategoryData: chartData.device_type_chart_data,
        }))
      } else {
        setState((prev) => ({
          ...prev,
          chartLoading: false,
          chartError: t("chartDataError", "Failed to load chart data"),
        }))
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      setState((prev) => ({
        ...prev,
        chartLoading: false,
        chartError: errorMessage,
      }))    } finally {
      setState((prev) => ({ ...prev, chartLoading: false }))
    }
  }
  
  // Load chart data when walkdown ID or site changes
  useEffect(() => {
    if (id && gdmNumber) {
      setInitialLoading(true);
      chartDataFetchedRef.current = false
      const walkdownIds = id.split(",")
      loadChartData(walkdownIds)
    }
  }, [id, gdmNumber, t])

  // Cleanup: Clear walkdown info from Redux when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearCurrentWalkdownInfo())
    }
  }, [dispatch])
  
  // Reset fetch refs when id or site changes to allow refetching
  useEffect(() => {
    walkdownDetailsFetchedRef.current = false
    chartDataFetchedRef.current = false
    lastWalkdownDetailsKeyRef.current = null;
    lastChartDataKeyRef.current = null;
  }, [id, gdmNumber])
  // Handle QR code generation for multiple devices
  const handleGenerateQrCode = async () => {
    const selectedDevices = state.filteredDevices.filter(device => 
      state.selectedRowIds.includes(String(device.id)) && device.deviceId && device.deviceId !== "" && device.gtinNumber && device.gtinNumber !== ""
    );
    const deviceIds = selectedDevices.map(device => device.deviceId).filter((id): id is string => !!id);

    if (deviceIds.length === 0) {
      console.warn('No valid device IDs found for QR code generation');
      return;
    }
    setQrCodeRequesting(true);
    setQrCodeRequested(false);
    setState(prev => ({ ...prev, anchorEl: null }));
    try {
      const deviceService = new DeviceService();

      // Prepare params for API call with user_id, walkdown_number, and site_name
      // Try multiple fields to find the employee ID
      const user_id = userData?.id || 
                      localStorage.getItem('userID') || 
                      '';
        const params = {
        device_ids: deviceIds,
        user_id: user_id,
        walkdown_number: state.walkdownNumbers[0] || '',
        site_name: siteName || ''
      };
 
      // Call the API to generate QR codes (backend will create notification)
      const response = await deviceService.generateQRCodes(params);
      
      if (response) {
        // Success - QR code generation request submitted
        setQrCodeRequesting(false);
        setQrCodeRequested(true);

        // Clear selection after successful generation
        setState(prev => ({ ...prev, selectedRowIds: [] }));

        // Hide success message after 5 seconds
        setTimeout(() => {
          setQrCodeRequested(false);
        }, 5000);

        // The notification will be sent by the backend
        // User will receive it in their notification center
      } else {
        // API call failed
        setQrCodeRequesting(false);
      }
    } catch (error) {
      console.error("QR Code Generation Error:", error);
      setQrCodeRequesting(false);
    }
  };
  // Handle QR code generation for a single device (from actions menu)
  const handleGenerateQrCodeForDevice = async (device: WalkdownDevice) => {
    // Check if device has valid device_id and gtin_number
    if (!device.deviceId || device.deviceId === "" || !device.gtinNumber || device.gtinNumber === "") {
      console.warn('Cannot generate QR code for device without device_id or gtin_number');
      return;
    }
    setQrCodeRequesting(true);
    setQrCodeRequested(false);
    setState(prev => ({ ...prev, anchorEl: null }));
    try {
      const deviceService = new DeviceService();

      // Prepare params for API call with user_id, walkdown_number, and site_name
      // Try multiple fields to find the employee ID
      const user_id = userData?.id || 
                      localStorage.getItem('userID') || 
                      '';
        const params = {
        device_ids: [device.deviceId],
        user_id: user_id,
        walkdown_number: state.walkdownNumbers[0] || '',
        site_name: siteName || ''
      };
      // Call the API to generate QR codes (backend will create notification)
      const response = await deviceService.generateQRCodes(params);
      
      if (response) {
        // Success - QR code generation request submitted
        setQrCodeRequesting(false);
        setQrCodeRequested(true);

        // Clear selection after successful generation
        setState(prev => ({ ...prev, selectedRowIds: [] }));

        // Hide success message after 5 seconds
        setTimeout(() => {
          setQrCodeRequested(false);
        }, 5000);
      } else {
        // API call failed
        setQrCodeRequesting(false);
      }
    } catch (error) {
      console.error("QR Code Generation Error:", error);
      setQrCodeRequesting(false);
    }
  };
  // Get current page row IDs for select-all-on-page
  const currentPageRowIds = useMemo(() => state.filteredDevices.map((row) => row.id), [state.filteredDevices])
  const selectableRowIds = useMemo(() => currentPageRowIds.filter(id => {
    const device = state.filteredDevices.find(d => d.id === id);
    return device && device.deviceId && device.deviceId !== "" && device.gtinNumber && device.gtinNumber !== "";
  }), [currentPageRowIds, state.filteredDevices]);

  const allSelectedOnPage = selectableRowIds.length > 0 && selectableRowIds.every(id => state.selectedRowIds.includes(String(id)))
  const someSelectedOnPage = selectableRowIds.some(id => state.selectedRowIds.includes(String(id)))
  // Individual row checkbox handler
  const handleCheckboxChange = (rowId: string, checked: boolean) => {
    const device = state.filteredDevices.find(d => d.id === rowId);
    if (!device || !device.deviceId || device.deviceId === "" || !device.gtinNumber || device.gtinNumber === "") return;

    setState((prev) => {
      if (checked) {
        if (!prev.selectedRowIds.includes(String(rowId))) {
          return { ...prev, selectedRowIds: [...prev.selectedRowIds, String(rowId)] }
        }
        return prev
      } else {
        return { ...prev, selectedRowIds: prev.selectedRowIds.filter((id) => id !== String(rowId)) }
      }
    })
  }

  // Header checkbox handler (select/deselect all on page)
  const handleHeaderCheckboxChange = (checked: boolean) => {
    if (!checked) {
      setState((prev) => ({
        ...prev,
        selectedRowIds: prev.selectedRowIds.filter(id => !selectableRowIds.map(String).includes(String(id)))
      }))
    } else {
      setState((prev) => {
        const prevSet = new Set(prev.selectedRowIds.map(String))
        const result = [...prev.selectedRowIds.map(String)]
        for (let i = 0; i < selectableRowIds.length; i++) {
          const idStr = String(selectableRowIds[i])
          if (!prevSet.has(idStr)) {
            result.push(idStr)
          }
        }
        return { ...prev, selectedRowIds: result }
      })
    }
  }
  // Update charts based on filtered devices
  const updateChartsFromFilteredDevices = (filteredDevices: WalkdownDevice[]) => {
    const deviceTypeCounts: Record<string, number> = {}
    const ratingCounts: Record<string, number> = {}
    const recommendationCounts: Record<string, number> = {}

    filteredDevices.forEach((device) => {
      if (device.deviceType) {
        deviceTypeCounts[device.deviceType] = (deviceTypeCounts[device.deviceType] || 0) + 1
      }
      if (device.walkdownRating) {
        ratingCounts[device.walkdownRating] = (ratingCounts[device.walkdownRating] || 0) + 1
      }
      if (device.recommendation) {
        recommendationCounts[device.recommendation] = (recommendationCounts[device.recommendation] || 0) + 1
      }
    })

    // Rebuild nested device type data structure based on original category data
    const rebuiltCategoryData: DeviceTypeCategoryData[] = state.originalDeviceTypeCategoryData.map(category => {
      const filteredDeviceTypes = category.device_types
        .map(dt => ({
          device_type: dt.device_type,
          count: deviceTypeCounts[dt.device_type] || 0
        }))
        .filter(dt => dt.count > 0); // Only include device types that have devices

      const total_count = filteredDeviceTypes.reduce((sum, dt) => sum + dt.count, 0);

      return {
        category: category.category,
        total_count,
        device_types: filteredDeviceTypes
      };
    }).filter(cat => cat.total_count > 0); // Only include categories that have devices

    const deviceTypeData = transformDeviceTypeData(rebuiltCategoryData)
    const ratingsData = transformRatingsData(
      Object.entries(ratingCounts).map(([name, count]) => ({ name, count }))
    )
    const recommendationsData = transformRecommendationsData(
      Object.entries(recommendationCounts).map(([name, count]) => ({ name, count }))
    )

    setState((prev) => ({
      ...prev,
      chartData: {
        deviceType: deviceTypeData,
        ratings: ratingsData,
        recommendations: recommendationsData,
      },
    }))
  }

  // Filter handling
  const handleFilter = (filters: WalkdownFilters): void => {
    let filtered = [...state.devices]

    if (filters.deviceTypes?.length) {
      filtered = filtered.filter((device) => filters.deviceTypes?.includes(device.deviceType))
    }

    if (filters.ratings?.length) {
      filtered = filtered.filter((device) => filters.ratings?.includes(device.walkdownRating))
    }

    if (filters.recommendations?.length) {
      filtered = filtered.filter((device) => filters.recommendations?.includes(device.recommendation))
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      filtered = filtered.filter((device) => 
        device.tag.toLowerCase().includes(searchTerm) || 
        device.serialNumber.toLowerCase().includes(searchTerm) || 
        device.deviceType.toLowerCase().includes(searchTerm)
      )
    }

    setState((prev) => ({ ...prev, filteredDevices: filtered }))
    updateChartsFromFilteredDevices(filtered)
  }
  // Chart slice selection handlers with reset functionality
  const handleDeviceTypeSliceSelect = (entry: LifecycleStatusCardData) => {
    // Level 1: Category selected
    if (!deviceTypeCategoryFilter && !deviceTypeFilter) {
      setDeviceTypeCategoryFilter(entry.name)
      // Don't filter devices yet, just drill into category
      return
    }
    
    // Level 2: Device type selected (category already selected)
    if (deviceTypeCategoryFilter && !deviceTypeFilter) {
      const isSame = deviceTypeFilter === entry.name
      
      if (isSame) {
        setDeviceTypeFilter(null)
        handleFilter({ 
          deviceTypes: [], 
          ratings: ratingFilter ? [ratingFilter] : [], 
          recommendations: recommendationFilter ? [recommendationFilter] : [] 
        })
      } else {
        setDeviceTypeFilter(entry.name)
        handleFilter({ 
          deviceTypes: [entry.name], 
          ratings: ratingFilter ? [ratingFilter] : [], 
          recommendations: recommendationFilter ? [recommendationFilter] : [] 
        })
      }
      return
    }
    
    // Level 3: Already at device type level, toggle filter
    if (deviceTypeFilter) {
      const isSame = deviceTypeFilter === entry.name
      
      if (isSame) {
        setDeviceTypeFilter(null)
        handleFilter({ 
          deviceTypes: [], 
          ratings: ratingFilter ? [ratingFilter] : [], 
          recommendations: recommendationFilter ? [recommendationFilter] : [] 
        })
      } else {
        setDeviceTypeFilter(entry.name)
        handleFilter({ 
          deviceTypes: [entry.name], 
          ratings: ratingFilter ? [ratingFilter] : [], 
          recommendations: recommendationFilter ? [recommendationFilter] : [] 
        })
      }
    }
  }

  const handleRatingSliceSelect = (entry: LifecycleStatusCardData) => {
    const isSame = ratingFilter === entry.name
    
    if (isSame) {
      setRatingFilter(null)
      handleFilter({ deviceTypes: deviceTypeFilter ? [deviceTypeFilter] : [], ratings: [], recommendations: recommendationFilter ? [recommendationFilter] : [] })
    } else {
      setRatingFilter(entry.name)
      handleFilter({ deviceTypes: deviceTypeFilter ? [deviceTypeFilter] : [], ratings: [entry.name], recommendations: recommendationFilter ? [recommendationFilter] : [] })
    }
  }

  const handleRecommendationSliceSelect = (entry: LifecycleStatusCardData) => {
    const isSame = recommendationFilter === entry.name
    
    if (isSame) {
      setRecommendationFilter(null)
      handleFilter({ deviceTypes: deviceTypeFilter ? [deviceTypeFilter] : [], ratings: ratingFilter ? [ratingFilter] : [], recommendations: [] })
    } else {
      setRecommendationFilter(entry.name)
      handleFilter({ deviceTypes: deviceTypeFilter ? [deviceTypeFilter] : [], ratings: ratingFilter ? [ratingFilter] : [], recommendations: [entry.name] })
    }
  }
  // Back button handlers for charts
  const handleDeviceTypeBack = () => {
    // If we're at device type level (level 3), go back to category level (level 2)
    if (deviceTypeFilter) {
      setDeviceTypeFilter(null)
      handleFilter({ 
        deviceTypes: [], 
        ratings: ratingFilter ? [ratingFilter] : [], 
        recommendations: recommendationFilter ? [recommendationFilter] : [] 
      })
      return
    }
    
    // If we're at category level (level 2), go back to all categories (level 1)
    if (deviceTypeCategoryFilter) {
      setDeviceTypeCategoryFilter(null)
      // No filtering needed at category level
      return
    }
  }

  const handleRatingBack = () => {
    setRatingFilter(null)
    handleFilter({ deviceTypes: deviceTypeFilter ? [deviceTypeFilter] : [], ratings: [], recommendations: recommendationFilter ? [recommendationFilter] : [] })
  }

  const handleRecommendationBack = () => {
    setRecommendationFilter(null)
    handleFilter({ deviceTypes: deviceTypeFilter ? [deviceTypeFilter] : [], ratings: ratingFilter ? [ratingFilter] : [], recommendations: [] })
  }

  // Custom checkbox column with header checkbox
  const checkboxColumn: GridColDef = {
    field: "select",
    headerName: "",
    renderHeader: () => {
      if (!multiSelectCheckboxPermission.canView) return null;
      const indeterminate = !allSelectedOnPage && someSelectedOnPage
      return (
        <Checkbox
          checked={allSelectedOnPage}
          indeterminate={indeterminate}
          onChange={(_, checked) => handleHeaderCheckboxChange(checked)}
          color={indeterminate ? 'primary' : 'secondary'}
          size="small"
          inputProps={{ 'aria-label': 'Select all rows on page' }}
        />
      )
    },
    width: 80,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) => {
      if (!multiSelectCheckboxPermission.canView) return null;
      const isSelectable = params.row.deviceId && params.row.deviceId !== "" && params.row.gtinNumber && params.row.gtinNumber !== "";
      return (
        <Checkbox
          checked={state.selectedRowIds.includes(params.row.id)}
          onChange={(_, checked) => handleCheckboxChange(params.row.id, checked)}
          color="secondary"
          size="small"
          disabled={!isSelectable}
          inputProps={{ 'aria-label': `Select row ${params.row.id}` }}
        />
      )
    }
  }

  // Columns definition
  const isMultiWalkdownView = (id && id.split(',').length > 1);
  const walkdownNumberColumn: GridColDef = {
    field: "walkdownNumber",
    headerName: t("walkdownNumber", "WALKDOWN NUMBER"),
    flex: 1,
    minWidth: 140,
    renderCell: (params) => (
      <Typography variant="body2">{params.value || "-"}</Typography>
    ),
  };
  const createdDateColumn: GridColDef = {
    field: "walkdownCreatedAt",
    headerName: t("createdDate", "CREATED DATE"),
    flex: 1,
    minWidth: 130,
    renderCell: (params) => (
      <Typography variant="body2">
        {params.value ? new Date(params.value).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "-"}
      </Typography>
    ),
  };
  const columns = useMemo<GridColDef[]>(
    () => [
      ...(multiSelectCheckboxPermission.canView ? [checkboxColumn] : []),
      ...(isMultiWalkdownView ? [walkdownNumberColumn] : []), // Conditionally add walkdown number column
      { field: "tag", headerName: t("tag", "TAG"), flex: 1, minWidth: 120 },
      {
        field: "serialNumber",
        headerName: t("serialNumber", "Serial Number"),
        flex: 1.2,
        minWidth: 150,
        renderCell: (params) => {
          const device = params.row as WalkdownDevice;
          const hasDeviceId = !!device.deviceId;
          
          // Determine which serial number to display based on device_id
          const displaySerialNumber = hasDeviceId ? device.deviceSerialNumber : device.serialNumber;
          const hasSerialNumber = displaySerialNumber && displaySerialNumber !== "-" && displaySerialNumber !== "" && displaySerialNumber !== "0";

          // If no device_id and no serial number, show "Link a Device"
          if (!hasDeviceId && !hasSerialNumber) {
            return (
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Link a Device" placement="top" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setState((prev) => ({ 
                        ...prev, 
                        openLinkDevicePopup: true,
                        selectedDevice: params.row as WalkdownDevice 
                      }))
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <AddCircleOutlineIcon sx={{ width: 16, height: 16, color: 'primary.main' }} />
                  </IconButton>
                </Tooltip>
                <Typography
                  variant="body2"
                  sx={{
                    color: "primary.main",
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" }
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setState((prev) => ({ 
                      ...prev, 
                      openLinkDevicePopup: true,
                      selectedDevice: params.row as WalkdownDevice 
                    }))
                  }}
                >
                  Link a Device
                </Typography>
              </Box>
            )
          }

          // If has serial number, show serial number with icon (edit if linked, add if not linked)
          return (
            <Box display="flex" alignItems="center" gap={1}>
              {hasDeviceId ? (
                <Tooltip title="Edit Device Link" placement="top" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setState((prev) => ({ 
                        ...prev, 
                        openLinkDevicePopup: true,
                        selectedDevice: params.row as WalkdownDevice 
                      }))
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <img src={iconEdit} alt="edit" style={{ width: 16, height: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Link a Device" placement="top" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      setState((prev) => ({ 
                        ...prev, 
                        openLinkDevicePopup: true,
                        selectedDevice: params.row as WalkdownDevice 
                      }))
                    }}
                    sx={{ p: 0.5 }}
                  >
                    <AddCircleOutlineIcon sx={{ width: 16, height: 16, color: 'primary.main' }} />
                  </IconButton>
                </Tooltip>
              )}
              {hasDeviceId ? (
                <Link
                  to={`/devices/${device.deviceId}`}
                  className="text-primary flex-start device-link"
                  style={{ textDecoration: 'none' }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {displaySerialNumber}
                  </Typography>
                </Link>
              ) : (
                <Typography variant="body2">
                  {displaySerialNumber}
                </Typography>
              )}
            </Box>
          )
        },
      },
      {
        field: "image",
        headerName: t("image", "IMAGE"),
        flex: 0.8,
        minWidth: 100,
        renderCell: (params) => {
          // Check if images array exists and has valid images with image_url
          const hasValidImages = params.row.images && 
            params.row.images.length > 0 && 
            params.row.images.some((img: any) => img.image_url && img.image_url.trim() !== "")
          
          if (!hasValidImages) {
            return <Typography variant="body2">-</Typography>
          }
          
          // Count only valid images with image_url
          const validImageCount = params.row.images.filter((img: any) => img.image_url && img.image_url.trim() !== "").length
          
          return (
            <Box display="flex" alignItems="center" gap={1} sx={{ cursor: "pointer" }}>
              <img src={iconImageView} alt="view" style={{ width: 16, height: 16 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "primary.main",
                  "&:hover": { textDecoration: "underline" },
                }}
                onClick={() => {
                  setState((prev) => ({ ...prev, selectedDevice: params.row as WalkdownDevice, openImageModal: true }))
                }}
              >
                ({validImageCount})
              </Typography>
            </Box>
          )
        },
      },
      { field: "deviceType", headerName: t("deviceType", "DEVICE TYPE"), flex: 1.2, minWidth: 150 },
      { 
        field: "walkdownRating", 
        headerName: t("walkdownRating", "WALKDOWN RATING"), 
        flex: 1, 
        minWidth: 150,        
        renderCell: (params) => {
          if (!params.value) {
            return <Typography variant="body2">-</Typography>
          }
          
          const rating = params.value?.toLowerCase();
          let color = '#9E9E9E'; // Default gray color
          
          if (rating === 'green') {
            color = '#00805A';
          } else if (rating === 'yellow') {
            color = '#F8B11E';
          } else if (rating === 'red') {
            color = '#D31245';
          }
          
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ minWidth: 16, minHeight: 16, bgcolor: color, borderRadius: '50%' }} />
              <Typography>{params.value}</Typography>
            </Box>
          );
        }
      },
      { field: "recommendation", headerName: t("recommendation", "RECOMMENDATION"), flex: 1.2, minWidth: 150 },
      { field: "comments", headerName: t("comments", "COMMENTS"), flex: 1.5, minWidth: 180 },
      ...(isMultiWalkdownView ? [createdDateColumn] : []), // Created Date shown left of actions in multi-walkdown view
    ],
    [checkboxColumn, t, state.selectedRowIds.length, multiSelectCheckboxPermission.canView, walkdownDeviceLinkingPermission.canEdit, isMultiWalkdownView]
  );
  useEffect(() => {
    if (!id) return
      if (!gdmNumber) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: t('noSiteSelected', 'Please select a site to view walkdown details'),
        devices: [],
        filteredDevices: [],
        walkdown: null,
        walkdownNumbers: []
      }))
      return
    }

    walkdownDetailsFetchedRef.current = false

    const fetchWalkdownDetails = async () => {
      const fetchKey = `${id}|${gdmNumber}`;
      if (lastWalkdownDetailsKeyRef.current === fetchKey && walkdownDetailsFetchedRef.current) return;
      lastWalkdownDetailsKeyRef.current = fetchKey;
      walkdownDetailsFetchedRef.current = true;

      try {
        const accessToken = await getAccessTokenSilently();
        setState((prev) => ({ ...prev, loading: true, error: null }))
        const walkdownIds = id.split(",")        
        const walkdownNumbers: string[] = []
        const walkdownCreationDates: string[] = []

        const walkdownService = new WalkdownService(undefined, accessToken)

        if (gdmNumber) {
          const walkdownData = await walkdownService.getWalkdowns({
            gdm_customer_number: gdmNumber,
          })

          if (walkdownData?.walkdown_records) {
            walkdownIds.forEach((walkdownId) => {
              const walkdownRecord = walkdownData.walkdown_records.find((record: any) => record.id === walkdownId.trim())
              if (walkdownRecord) {
                walkdownNumbers.push(walkdownRecord.number)
                walkdownCreationDates.push(walkdownRecord.creation_date)
              }
            })
          }
        }

        setWalkdownDates(walkdownCreationDates);        
        const devicesResponse = await walkdownService.getWalkdownDevices(walkdownIds)
        let devices: WalkdownDevice[] = []
        if (devicesResponse?.walkdown_devices) {
          devices = devicesResponse.walkdown_devices.map((apiDevice: any) => ({
            id: apiDevice.id,
            deviceId: apiDevice.device_id,
            tag: apiDevice.tag_number,
            serialNumber: apiDevice.serial_number,
            deviceSerialNumber: apiDevice.device_serial_number,
            deviceType: apiDevice.device_type,
            walkdownRating: apiDevice.visual_health_rating,
            recommendation: apiDevice.recommendation,
            comments: apiDevice.comments || "",
            images: apiDevice.images || [],
            image: `(${apiDevice.images?.length || 0})`,
            device: apiDevice.device,
            walkdownNumber: apiDevice.walkdown_number, // <-- add walkdown number
            walkdownCreatedAt: apiDevice.walkdown_created_at || null, // <-- add walkdown created date
            gtinNumber: apiDevice.gtin_number || null // <-- add GTIN number
          }))
        }

        const walkdownData = {
          id,
          date: walkdownCreationDates[0] || "",
          devices: devices,
        }

        setState((prev) => ({
          ...prev,
          walkdown: walkdownData,
          walkdownNumbers,
          devices: devices,
          filteredDevices: devices,
          loading: false,
        }))

        if (walkdownNumbers.length > 0) {
          dispatch(
            setCurrentWalkdownInfo({
              walkdownIds: walkdownIds,
              walkdownNumbers: walkdownNumbers,
            })
          )
        }
        
        walkdownDetailsFetchedRef.current = true
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        setState((prev) => ({
          ...prev,
          loading: false,
          error: errorMessage,
        }))      } finally {
        setState((prev) => ({ ...prev, loading: false }))
        setInitialLoading(false);
      }
    }
      if (!walkdownDetailsFetchedRef.current) {
      fetchWalkdownDetails()
    }
  }, [id, gdmNumber, dispatch, t])

  // Refresh walkdown data after device link/unlink
  const refreshWalkdownData = async () => {
    if (!id) return;
    try {      
      const accessToken = await getAccessTokenSilently();
      const walkdownIds = id.split(',').filter((wid) => wid);
      const walkdownService = new WalkdownService(undefined, accessToken);
      const devicesResponse = await walkdownService.getWalkdownDevices(walkdownIds);
        let devices: WalkdownDevice[] = [];
      if (devicesResponse?.walkdown_devices) {
        devices = devicesResponse.walkdown_devices.map((apiDevice: any) => ({
          id: apiDevice.id,
          deviceId: apiDevice.device_id,
          tag: apiDevice.tag_number,
          serialNumber: apiDevice.serial_number,
          deviceSerialNumber: apiDevice.device_serial_number,
          deviceType: apiDevice.device_type,
          walkdownRating: apiDevice.visual_health_rating,
          recommendation: apiDevice.recommendation,
          comments: apiDevice.comments || "",
          images: apiDevice.images || [],
          image: `(${apiDevice.images?.length || 0})`,
          device: apiDevice.device,
          walkdownNumber: apiDevice.walkdown_number, // <-- add walkdown number
          gtinNumber: apiDevice.gtin_number || null // <-- add GTIN number
        }));
      }
      
      setState((prev) => ({
        ...prev,
        devices: devices,
        filteredDevices: devices,
        selectedDevice: null,
        loading: false
      }));
    } catch (error) {
      console.error('Error refreshing walkdown data:', error);
    } finally {
      setState(prev => ({ ...prev, loading: false }));
    }
  };
  const actionsColumn = useMemo(
    () => ({
      headerName: t("actions", "ACTIONS"),
      actions: (row: WalkdownDevice) => {
        const hasDeviceId = row.deviceId && row.deviceId !== "";
        const hasGtinNumber = row.gtinNumber && row.gtinNumber !== "";
        const selectedEligibleCount = state.filteredDevices.filter(device => 
          device?.id && state.selectedRowIds.includes(String(device.id)) && device.deviceId && device.deviceId !== "" && device.gtinNumber && device.gtinNumber !== ""
        ).length;
        const canGenerateQR = generateQRCodePermission.canView && hasGtinNumber && (hasDeviceId || selectedEligibleCount > 0);
        const labelBase = t("generateQrCode", "Generate QR Code");
        const label = selectedEligibleCount > 0 ? `${labelBase}(${selectedEligibleCount})` : labelBase;

        return [
          {
            label,
            onClick: () => {
              if (!canGenerateQR) return;
              if (selectedEligibleCount > 0) {
                handleGenerateQrCode();
              } else if (hasDeviceId && hasGtinNumber) {
                handleGenerateQrCodeForDevice(row);
              }
            },
            disabled: !canGenerateQR
          }
        ]
      },
      width: 70,
    }),
    [t, handleGenerateQrCodeForDevice, handleGenerateQrCode, generateQRCodePermission.canView, state.selectedRowIds, state.filteredDevices]
  );

  const handleRowSelect = (rowIds: any) => {
    const selectedIds = Array.isArray(rowIds) ? rowIds : Array.from(rowIds?.ids)
    const selected = state.filteredDevices.filter((row: any) => selectedIds.includes(row.id))
    setState((prev) => ({ ...prev, selectedRowIds: selected.map((row) => String(row.id)) }))
  }

  function handleSuccessWalkdownLinkDevice(type: 'link' | 'unlink' = 'link'): void {
    if (type === 'link') {
      setState((prev) => ({ ...prev, loading: true, openLinkDevicePopup: false, selectedDevice: null, successWalkdownLinkDevice: true }))
    } else {
      setState((prev) => ({ ...prev, loading: true, openLinkDevicePopup: false, selectedDevice: null }));
      setShowUnlinkSuccess(true);    }
    refreshWalkdownData();
  }

  // Show full page loader during initial load
  if (initialLoading) {
    return <FullPageLoader message={t("loadingWalkdownDetails", "Loading walkdown details...")} />;
  }

  return (
    <div data-testid="walkdown-details-root" className="page page-walkdown-details">
      <BreadcrumbsComponent />
      <div className="page-content">
        <Grid container spacing={2} pb={2}>
          <Grid size={12}>
            <Suspense fallback={<LoadingFallback />}>
              <div data-lazy-component="WalkdownsHeader">
                <WalkdownDetailsHeader
                  filterDevices={handleFilter}
                  paginationModel={paginationModel}
                  pageName={t("walkdownDevices", "Walkdown Devices")}
                  effectiveRowCount={state.filteredDevices.length}
                      totalRowCount={state.devices.length}
                      pageSizeOptions={[10, 25, 50, 75, 100]}
                      onPaginationModelChange={setPaginationModel}
                      walkdownNumbers={state.walkdownNumbers}
                      walkdownDates={walkdownDates}
                    />
                  </div>
                </Suspense>
              </Grid>
            </Grid>            <Grid container spacing={1} pb={2}>
              <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                {state.loading ? (
                  <ShimmerChartCard />
                ) : (
                  <Suspense fallback={<ShimmerChartCard />}>
                    {(() => {
                      // Determine which data to show based on drill-down level
                      let chartData: LifecycleStatusCardData[] = state.chartData.deviceType;
                      let showBackButton = false;
                      
                      // Level 2: Show device types for selected category
                      if (deviceTypeCategoryFilter && !deviceTypeFilter) {
                        const selectedCategory = state.chartData.deviceType.find(
                          cat => cat.name === deviceTypeCategoryFilter
                        );
                        if (selectedCategory && selectedCategory.children) {
                          chartData = selectedCategory.children;
                        }
                        showBackButton = true;
                      }
                      
                      // Level 3: Show single device type after selection
                      if (deviceTypeFilter && deviceTypeCategoryFilter) {
                        const selectedCategory = state.chartData.deviceType.find(
                          cat => cat.name === deviceTypeCategoryFilter
                        );
                        if (selectedCategory && selectedCategory.children) {
                          const selectedDeviceType = selectedCategory.children.find(
                            dt => dt.name === deviceTypeFilter
                          );
                          if (selectedDeviceType) {
                            chartData = [selectedDeviceType];
                          }
                        }
                        showBackButton = true;
                      }
                      
                      const highest = getHighestPercentAndName(chartData);
                      return (
                        <LifecycleStatusCard
                          title={t("deviceTypeCategory", "DEVICE TYPE CATEGORY")}
                          data={chartData.map(entry => ({
                            ...entry,
                            name: t(`deviceType_${entry.name}`, entry.name)
                          }))}
                          centerPercent={highest.percent}
                          centerText={<Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>{t(`deviceType_${highest.name}`, highest.name)}</Typography>}
                          onSliceSelect={handleDeviceTypeSliceSelect}
                          onBack={handleDeviceTypeBack}
                          drillMode="back"
                          forceShowBackButton={showBackButton}
                          circleSize="small"
                        />
                      );
                    })()}
                  </Suspense>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                {state.loading ? (
                  <ShimmerChartCard />
                ) : (
                  <Suspense fallback={<ShimmerChartCard />}>
                    {(() => {
                      const highest = getHighestPercentAndName(state.chartData.ratings);
                      return (
                        <LifecycleStatusCard
                          title={t("deviceWalkdownRatings", "DEVICE WALKDOWN RATINGS")}
                          data={state.chartData.ratings.map(entry => ({
                            ...entry,
                            name: t(`walkdowns_status_${entry.name.toUpperCase()}`, entry.name)
                          }))}
                          centerPercent={highest.percent}
                          centerText={<Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>{t(`walkdowns_status_${highest.name.toUpperCase()}`, highest.name)}</Typography>}
                          onSliceSelect={handleRatingSliceSelect}
                          onBack={handleRatingBack}
                          drillMode="back"
                          forceShowBackButton={ratingFilter !== null}
                          circleSize="small"
                        />
                      );
                    })()}
                  </Suspense>
                )}
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                {state.loading ? (
                  <ShimmerChartCard />
                ) : (
                  <Suspense fallback={<ShimmerChartCard />}>
                    {(() => {
                      const highest = getHighestPercentAndName(state.chartData.recommendations);
                      return (
                        <LifecycleStatusCard
                          title={t("walkdownRecommendations", "WALKDOWN RECOMMENDATIONS")}
                          data={state.chartData.recommendations.map(entry => ({
                            ...entry,
                            name: t(`walkdowns_recommendation_${entry.name.replace(/\s|\//g, "")}`, entry.name)
                          }))}
                          centerPercent={highest.percent}
                          centerText={<Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500 }}>{highest.name === 'No Action Required' ? t('walkdowns_recommendations_no_actions_required', highest.name) : t(`walkdowns_recommendation_${highest.name.replace(/\s|\//g, "")}`, highest.name)}</Typography>}
                          onSliceSelect={handleRecommendationSliceSelect}
                          onBack={handleRecommendationBack}
                          drillMode="back"
                          forceShowBackButton={recommendationFilter !== null}
                          circleSize="small"
                        />
                      );
                    })()}
                  </Suspense>
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} mt={2}>
              <Grid size={12}>
                <Typography variant="h4" className="walkdown-devices-title" sx={{ marginBottom: "10px", fontWeight: '400' }}>
                  {t("walkdownDevices", "Walkdown Devices")}
                </Typography>

                {qrCodeRequesting && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <MiniLoader size={40} />
                    <Typography variant="h6">
                      {t("qrCodeRequesting", "Please wait... Sending QR Code Request...")}
                    </Typography>
                  </Box>
                )}

                {qrCodeRequested && (
                  <Box sx={{ mb: 2 }}>
                    <Alert severity="success">
                      {t("qrCodeSuccess", "Your QR code request has been sent. You will receive a notification when it has processed.")}
                    </Alert>
                  </Box>
                )}

                {state.selectedRowIds.length > 0 && (
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    mb: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}>
                    <Typography variant="body2">
                      {state.selectedRowIds.length} {t("device", "device")}{state.selectedRowIds.length > 1 ? 's' : ''} {t("selected", "selected")}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>                      
                      {generateQRCodePermission.canView && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleGenerateQrCode}
                          disabled={
                            qrCodeRequesting || 
                            state.filteredDevices.filter(device => 
                              device?.id && state.selectedRowIds.includes(String(device.id)) && device.deviceId && device.deviceId !== "" && device.gtinNumber && device.gtinNumber !== ""
                            ).length === 0
                          }
                        >
                          {t("generateQrCode", "Generate QR Code")} ({state.selectedRowIds.length})
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setState((prev) => ({ ...prev, selectedRowIds: [] }))
                        }}
                      >
                        {t("clearSelection", "Clear Selection")}
                      </Button>
                    </Box>
                  </Box>
                )}

                <Suspense fallback={<ShimmerDataGrid />}>
                  <CommonDataGrid
                    rows={state.filteredDevices}
                    columns={columns}
                    loading={state.loading}
                    pageName="Devices"
                    totalRowCount={state.filteredDevices.length}
                    error={state.error}
                    paginationModel={paginationModel}
                    paginationMode="client"
                    onPaginationModelChange={setPaginationModel}
                    noRowsMessage={t("noDevicesFound", "No devices found")}
                    enableSearch={false}                  
                    pageSizeOptions={[10, 25, 50, 75, 100]}
                    onRowSelect={handleRowSelect}
                    actionsColumn={actionsColumn}
                  />
                </Suspense>
              </Grid>
            </Grid>

            {/* Modals and popups */}
            {state.selectedDevice && (
              <WalkdownImagesSection
                open={state.openImageModal}
                onClose={() => setState((prev) => ({ ...prev, openImageModal: false }))}
                deviceTag={state.selectedDevice.tag}
                walkdownNumber={state.selectedDevice.walkdownNumber || ""}
                date={state.walkdown?.date || ""}
                images={state.selectedDevice.images || []}
              />
            )}

            {state.successWalkdownUnlinkDevice && (
              <MessageModal
                open={state.successWalkdownUnlinkDevice}
                title={t("walkUnlinkDeviceModalTitle", "Unlink Device")}
                message={t("walkUnlinkDeviceSuccessMessage", "Successfully unlinked device from walkdown.")}
                onClose={() => setState((prev) => ({ ...prev, successWalkdownUnlinkDevice: false }))}
              />
            )}

            {state.successWalkdownLinkDevice && (
              <MessageModal
                open={state.successWalkdownLinkDevice}
                title={t("walkLinkDeviceModalTitle", "Link Device")}
                message={t("walkLinkDeviceSuccessMessage", "Successfully linked device to walkdown.")}
                onClose={() => {
                  setState((prev) => ({ ...prev, successWalkdownLinkDevice: false }))
                }}
              />
            )}

            {showUnlinkSuccess && (
              <MessageModal
                open={showUnlinkSuccess}
                onClose={() => { setShowUnlinkSuccess(false); }}
                title={t("walkUnlinkDeviceModalTitle", "Unlink Device")}
                message={t("walkUnlinkDeviceSuccessMessage", "Successfully unlinked device from walkdown.")}
              />
            )}            
            <WalkdownLinkDevicePopup
              open={state.openLinkDevicePopup}
              onClose={() => setState((prev) => ({ ...prev, openLinkDevicePopup: false, selectedDevice: null }))}
              currentDevice={state.selectedDevice}
              onSuccess={(type = 'link') => handleSuccessWalkdownLinkDevice(type)}
              isLinked={!!state.selectedDevice?.device}
            />
      </div>
    </div>
  )
}

export default WalkdownDetails
